// src/generators/dzial5_trygonometria/temat2_funkcje_rozszerzone/podtemat2_wzory_redukcyjne_types/ReduceCosApproxTask.js
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// ===== helpers =====
function degToRad(deg) {
  return (deg * Math.PI) / 180;
}
// podgląd LaTeX spójny z ApproximateTrigValueTask
function formatLatexPreview(input) {
  return String(input || "")
    .replaceAll("*", "\\cdot ")
    .replaceAll("/", "\\div ")
    .replaceAll("sqrt", "\\sqrt")
    .replaceAll("-", " - ")
    .replaceAll("+", " + ");
}

// ===== task =====
export function generateTask() {
  const excluded = [120, 135, 150, 180];
  let alpha;
  do {
    alpha = Math.floor(Math.random() * (179 - 91 + 1)) + 91; // 91–179
  } while (excluded.includes(alpha));

  const acuteAngle = 180 - alpha; // 1..89
  // cos(180° - α) = -cos α  -> wynik ujemny (dla 91..179) i do 3 miejsc
  const approxValue = -Math.round(Math.cos(degToRad(acuteAngle)) * 1000) / 1000;

  return {
    type: "ReduceCosApproxTask",
    data: { alpha, acuteAngle, approxValue },
  };
}

export function renderTask({ data }) {
  const { alpha } = data;
  return (
    <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
      Zredukuj kąt i podaj wartość przybliżoną z dokładnością do{" "}
      <InlineMath math="0{,}001" />:
      <InlineMath math={`\\ \\cos ${alpha}^\\circ`} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={`\\cos ${task?.data?.alpha}^\\circ`}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const { alpha, approxValue } = data;

  // meta dla renderUserAnswer (dynamiczny kąt)
  if (typeof userAnswer === "object") {
    userAnswer.func = "cos";
    userAnswer.angle = alpha;
  }

  if (!userAnswer || typeof userAnswer !== "object") return false;
  const given = userAnswer.parsed;
  if (typeof given !== "number") return false;

  // tolerancja 0.001
  return Math.abs(approxValue - given) <= 0.001;
}

export function renderAnswer({ data }) {
  const { alpha, approxValue } = data;
  return (
    <InlineMath math={`\\cos ${alpha}^\\circ \\approx ${approxValue.toFixed(3)}`} />
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const angle = typeof userAnswer === "object" ? userAnswer.angle : undefined;

  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  // bez nawiasów przy kącie
  const label = angle != null ? `\\cos ${angle}^\\circ` : "";
  return (
    <InlineMath math={`\\displaystyle ${label} = ${formatLatexPreview(raw)}`} />
  );
}

export function renderExplanation({ data }) {
  const { alpha, acuteAngle, approxValue } = data;
  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Korzystamy ze wzoru redukcyjnego:
        <BlockMath math={`\\cos(180^\\circ - \\alpha) = -\\cos \\alpha`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Podstawiamy:
        <BlockMath math={`\\cos ${alpha}^\\circ = -\\cos(180^\\circ - ${alpha}^\\circ)`} />
        <BlockMath math={`\\cos ${alpha}^\\circ = -\\cos ${acuteAngle}^\\circ`} />
      </div>

      <div>
        <strong>Krok 3:</strong> Obliczamy wartość przybliżoną:
        <BlockMath math={`\\cos ${alpha}^\\circ \\approx ${approxValue.toFixed(3)}`} />
      </div>
    </div>
  );
}
